import React, { useEffect, useState } from 'react';
import {
  Typography,
  LinearProgress,
  Button,
  Avatar,
  Card,
  CardContent
} from '@mui/material';
import DashboardLayout from '../Sidebar/DashboardLayout';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import './Dashboard.css';
import Chatbox from '../../commun/ChatBox';
import { CheckCircleIcon, ThumbsUpIcon } from 'lucide-react';
import axios from 'axios';
import BusinessIcon from '@mui/icons-material/Business';
import { useNavigate } from 'react-router-dom';

// Chart.js
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title
} from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';
import { Link } from 'react-router-dom';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title
);

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const userName = localStorage.getItem('userType') || 'Utilisateur';
  const userRole = localStorage.getItem('userType');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/dashboard/stats`);
        setStats(res.data);
      } catch (error) {
        console.error("Erreur de chargement des données :", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statsData = [
    {
      label: 'Missions prises',
      value: stats?.totals?.missions || 0,
      className: 'stat-missions',
      icon: <AssignmentIcon className="stat-badge-icon" />,
    },
    {
      label: 'Candidats',
      value: stats?.totals?.candidats || 0,
      className: 'stat-candidats',
      icon: <PeopleIcon className="stat-badge-icon" />,
    },
    {
      label: 'Recruteurs',
      value: stats?.totals?.recruteurs || 0,
      className: 'stat-entretiens',
      icon: <ThumbsUpIcon className="stat-badge-icon" />,
    },
    {
      label: 'Entreprises',
      value: stats?.totals?.entreprises || 0,
      className: 'stat-recrutes',
      icon: <CheckCircleIcon className="stat-badge-icon" />,
    },
  ];

  const pieData = {
    labels: ['En cours', 'Recruté', 'Refusé'],
    datasets: [
      {
        label: 'Candidatures',
        data: stats
          ? [
              (stats.candidaturesParStatut?.['à traiter'] || 0) +
              (stats.candidaturesParStatut?.['Accepté pour entretien'] || 0),

              stats.candidaturesParStatut?.['Recrutement confirmé'] || 0,

              (stats.candidaturesParStatut?.['Refusé avant entretien'] || 0) +
              (stats.candidaturesParStatut?.['Non retenu après entretien'] || 0)
            ]
          : [0, 0, 0],
        backgroundColor: ['#42a5f5', '#66bb6a', '#ef5350'],
        borderWidth: 1,
      },
    ],
  };

  const lineLabels = [
    'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
    'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'
  ];

  const lineData = {
    labels: lineLabels,
    datasets: [
      {
        label: 'En cours',
        data: stats
          ? stats.candidaturesParMois.map((m) => m?.enCours ?? 0)
          : new Array(12).fill(0),
        borderColor: '#42a5f5',
        backgroundColor: '#42a5f5',
        tension: 0.3,
        fill: false,
      },
      {
        label: 'Recruté',
        data: stats
          ? stats.candidaturesParMois.map((m) => m?.recrute ?? 0)
          : new Array(12).fill(0),
        borderColor: '#66bb6a',
        backgroundColor: '#66bb6a',
        tension: 0.3,
        fill: false,
      },
      {
        label: 'Refusé',
        data: stats
          ? stats.candidaturesParMois.map((m) => m?.refuse ?? 0)
          : new Array(12).fill(0),
        borderColor: '#ef5350',
        backgroundColor: '#ef5350',
        tension: 0.3,
        fill: false,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: {
        display: true,
        text: 'Candidatures par mois (année complète)'
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0
      }
    }
  };

  return (
    <DashboardLayout>
      <div className="dashboard-wrapper">
        <Typography variant="h4" className="dashboard-heading">
          Bienvenue {userName} <span className="wave">👋</span>
        </Typography>

        {/* Statistiques */}
        <div className="dashboard-rec">
          <Typography variant="h5" mb={2}>Statistiques</Typography>
          {loading ? (
            <LinearProgress />
          ) : (
            <div className="stats-badge-flex guide-stats">
              {statsData.map((stat, i) => (
                <div className={`stat-badge-flat ${stat.className}`} key={i}>
                  <span className="stat-badge-icon">{stat.icon}</span>
                  <span className="stat-badge-content">
                    <span className="stat-badge-label">{stat.label}</span>
                    <span className="stat-badge-value">{stat.value || 0}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Graphiques */}
        <div style={{ marginTop: '40px' }}>
          <Typography variant="h5" gutterBottom>Visualisation Graphique</Typography>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
            <div style={{ flex: 1, minWidth: 300 }}>
              <Pie data={pieData} />
            </div>
            <div style={{ flex: 2, minWidth: 500 }}>
              <Line data={lineData} options={lineOptions} />
            </div>
          </div>
        </div>

        <div className="dashboard-cards">
          <Card className="dashboard-side-card recruteur-card-modern niveau-card-modern half-width">
            <div className="side-icon-wrap blue-bg">
              <PeopleIcon className="side-card-icon blue-icon" />
            </div>

            <CardContent>
              <div className="side-card-label">Top Recruteurs</div>

              {(() => {
                if (!stats || !stats.details) {
                  return <div className="loading-text">Chargement...</div>;
                }

                const topRecruteurs = stats.details.topRecruteurs
                  .filter((r) => r.totalTalentsRecruited > 0)
                  .slice(0, 3);

                if (topRecruteurs.length === 0) {
                  return <div className="loading-text">No top match</div>;
                }

                return topRecruteurs.map((recruteur) => {
                  const photoUrl = recruteur.photo
                    ? `${API_BASE}${recruteur.photo}?${Date.now()}`
                    : null;

                  return (
                    <div key={recruteur.id} className="recruteur-item">
                      <Avatar
                        src={photoUrl}
                        alt={`${recruteur.firstName} ${recruteur.lastName}`}
                        className="recruteur-avatar"
                      >
                        {!photoUrl &&
                          `${recruteur.firstName.charAt(0)}${recruteur.lastName.charAt(0)}`}
                      </Avatar>

                      <div>
                        <div className="side-card-value name-text">
                          <Link to={`/profil/${recruteur.id}`} className="recruteur-link">
                            {recruteur.firstName} {recruteur.lastName}
                          </Link>
                        </div>
                        <div className="side-card-desc email-text">{recruteur.email}</div>
                        <div className="side-card-desc recruited-text">
                          {recruteur.totalTalentsRecruited} talents recrutés
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            <Button
              onClick={() => navigate("/admin/recruteurs")}
              size="small"
              variant="outlined"
              className="side-card-btn"
              style={{ marginTop: '16px' }}
            >
              En savoir plus
            </Button>
            </CardContent>
          </Card>

          <Card className="dashboard-side-card prime-card-modern half-width">
            <div className="side-icon-wrap purple-bg">
              <BusinessIcon className="side-card-icon" />
            </div>

            <CardContent>
              <div className="side-card-label">Top Entreprises</div>

              {(() => {
                if (!stats || !stats.details) {
                  return <div className="loading-text">Chargement...</div>;
                }

                const topEntreprises = (stats.details.topEntreprises || []).slice(0, 5);
                if (topEntreprises.length === 0) {
                  return <div className="loading-text">Aucune entreprise</div>;
                }

                return topEntreprises.map((e) => {
                  const logoUrl = e.logo ? `${API_BASE}${e.logo}?${Date.now()}` : null;

                  return (
                    <div key={e.entrepriseId} className="entreprise-item">
                      <Avatar src={logoUrl} alt={e.NomEntreprise} className="entreprise-avatar">
                        {!logoUrl && (e.NomEntreprise?.[0] || "E")}
                      </Avatar>

                      <div>
                        <div className="side-card-value name-text">
                          <Link to={`/entreprise/${e.entrepriseId}`} className="recruteur-link">
                            {e.NomEntreprise}
                          </Link>
                        </div>
                        <div className="side-card-desc email-text">
                          {e.totalCandidatures} candidatures à traiter
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
              <Button
                onClick={() => navigate("/admin/entreprises")}
                size="small"
                variant="outlined"
                className="side-card-btn"
              >En savoir plus</Button>
            </CardContent>
          </Card>
        </div>

      </div>

      {/* Chatbox */}
      {userRole !== 'agent' && <Chatbox userRole={userRole} />}
    </DashboardLayout>
  );
};

export default Dashboard;
